import { useEffect, useState } from 'react';
import type { Package, SavedCard } from '@aslgo/shared';
import { getPaymentMethod, chargeSavedCard, confirmPurchase, createPurchaseIntent } from '../api/client.js';
import { getStripe } from '../lib/stripe.js';

export function usePackagePurchase(onBalance: (minutes: number) => void) {
  const [card, setCard] = useState<SavedCard | null | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [purchase, setPurchase] = useState<{ clientSecret: string; paymentIntentId: string } | null>(null);
  // A saved-card purchase awaiting the user's in-app confirmation (see
  // ConfirmPurchaseDialog). Null when no confirmation is pending.
  const [pendingBuy, setPendingBuy] = useState<Package | null>(null);

  useEffect(() => {
    let active = true;
    getPaymentMethod().then((r) => { if (active) setCard(r.card); }).catch(() => { if (active) setCard(null); });
    return () => { active = false; };
  }, []);

  async function buy(pkg: Package) {
    if (card === undefined) return;
    setError(null); setInfo(null);
    if (card) {
      // Ask for confirmation via the in-app dialog; the charge runs in confirmBuy().
      setPendingBuy(pkg);
    } else {
      setSubmitting(true);
      try { setPurchase(await createPurchaseIntent(pkg.id)); }
      catch (e) { setError((e as Error).message); } finally { setSubmitting(false); }
    }
  }

  async function confirmBuy() {
    const pkg = pendingBuy;
    if (!pkg) return;
    setPendingBuy(null);
    setSubmitting(true);
    try {
      const charge = await chargeSavedCard(pkg.id);
      if (charge.status === 'requires_action' && charge.clientSecret) {
        const stripe = await getStripe();
        if (!stripe) throw new Error('Payments are unavailable right now.');
        const { error: actionError } = await stripe.handleNextAction({ clientSecret: charge.clientSecret });
        if (actionError) throw new Error(actionError.message ?? 'Card authentication failed.');
      }
      const res = await confirmPurchase(charge.paymentIntentId);
      onBalance(res.sessionMinutes); setInfo(`Added ${res.added} minutes.`);
    } catch (e) { setError((e as Error).message); } finally { setSubmitting(false); }
  }

  function onPurchased(sessionMinutes: number, added: number) {
    onBalance(sessionMinutes); setInfo(`Added ${added} minutes.`); setPurchase(null);
    getPaymentMethod().then((r) => setCard(r.card)).catch(() => { /* best-effort refresh */ });
  }

  return {
    card, setCard, submitting, error, info, purchase, buy, onPurchased,
    closePurchase: () => setPurchase(null),
    pendingBuy, confirmBuy, cancelBuy: () => setPendingBuy(null),
  };
}
